/**
 * Request queue interceptor for managing concurrency and backpressure
 */

import { Interceptor, RpcEnvelope, RpcResponse } from "../types/protocol";

export interface RequestQueueConfig {
  maxConcurrentRequests: number;
  maxQueueSize: number;
  requestTimeout: number;
}

export interface RequestQueueMetrics {
  activeRequests: number;
  queuedRequests: number;
  totalProcessed: number;
  totalRejected: number;
  averageWaitTime: number;
}

interface QueuedRequest {
  request: RpcEnvelope;
  next: () => Promise<any>;
  resolve: (value: any) => void;
  reject: (error: Error) => void;
  queueTime: number;
  timeout?: ReturnType<typeof setTimeout>;
  priority: "normal" | "high";
}

class RequestQueue {
  private activeRequests = 0;
  private queue: QueuedRequest[] = [];
  private totalProcessed = 0;
  private totalRejected = 0;
  private totalWaitTime = 0;
  private destroyed = false;

  constructor(private config: RequestQueueConfig) {}

  async process(
    request: RpcEnvelope,
    next: () => Promise<any>,
    options?: { priority?: "normal" | "high" },
  ): Promise<any> {
    if (this.destroyed) {
      throw new Error("Request queue destroyed");
    }

    // If we have capacity, process immediately
    if (this.activeRequests < this.config.maxConcurrentRequests) {
      this.activeRequests++;
      return this.executeRequest(request, next);
    }

    // Check if queue is full
    if (this.queue.length >= this.config.maxQueueSize) {
      this.totalRejected++;
      throw new Error("Request queue is full");
    }

    // Queue the request
    return new Promise<any>((resolve, reject) => {
      const queuedRequest: QueuedRequest = {
        request,
        next,
        resolve,
        reject,
        queueTime: Date.now(),
        priority: options?.priority || "normal",
      };

      // Set timeout
      queuedRequest.timeout = setTimeout(() => {
        this.removeFromQueue(queuedRequest);
        reject(new Error("Request timeout"));
      }, this.config.requestTimeout);

      // Add to queue based on priority
      if (queuedRequest.priority === "high") {
        // Add to front of queue
        this.queue.unshift(queuedRequest);
      } else {
        // Add to end of queue
        this.queue.push(queuedRequest);
      }
    });
  }

  private async executeRequest(
    _request: RpcEnvelope,
    next: () => Promise<any>,
  ): Promise<any> {
    try {
      const result = await next();
      this.totalProcessed++;
      return result;
    } finally {
      this.activeRequests--;
      // Use setTimeout to ensure metrics are consistent before processing next
      setTimeout(() => this.processNext(), 0);
    }
  }

  private processNext(): void {
    if (
      this.queue.length === 0 ||
      this.activeRequests >= this.config.maxConcurrentRequests
    ) {
      return;
    }

    const queuedRequest = this.queue.shift()!;

    // Clear timeout
    if (queuedRequest.timeout) {
      clearTimeout(queuedRequest.timeout);
    }

    // Calculate wait time
    const waitTime = Date.now() - queuedRequest.queueTime;
    this.totalWaitTime += waitTime;

    // Increment active requests before executing
    this.activeRequests++;

    // Execute the request
    this.executeRequest(queuedRequest.request, queuedRequest.next)
      .then(queuedRequest.resolve)
      .catch(queuedRequest.reject);
  }

  private removeFromQueue(queuedRequest: QueuedRequest): void {
    const index = this.queue.indexOf(queuedRequest);
    if (index >= 0) {
      this.queue.splice(index, 1);
    }
  }

  getMetrics(): RequestQueueMetrics {
    const averageWaitTime =
      this.totalProcessed > 0 ? this.totalWaitTime / this.totalProcessed : 0;

    return {
      activeRequests: this.activeRequests,
      queuedRequests: this.queue.length,
      totalProcessed: this.totalProcessed,
      totalRejected: this.totalRejected,
      averageWaitTime,
    };
  }

  destroy(): void {
    this.destroyed = true;

    // Reject all queued requests
    for (const queuedRequest of this.queue) {
      if (queuedRequest.timeout) {
        clearTimeout(queuedRequest.timeout);
      }
      // Use setTimeout to avoid synchronous rejection during teardown
      setTimeout(() => {
        queuedRequest.reject(new Error("Request queue destroyed"));
      }, 0);
    }

    this.queue.length = 0;
  }
}

export class RequestQueueInterceptor implements Interceptor {
  public readonly name = "RequestQueueInterceptor";
  public readonly order = 40; // Run after auth but before logging

  private defaultConfig: RequestQueueConfig = {
    maxConcurrentRequests: 10,
    maxQueueSize: 50,
    requestTimeout: 30000,
  };

  private queues = new Map<string, RequestQueue>();
  private serviceConfigs = new Map<string, RequestQueueConfig>();

  constructor(config?: Partial<RequestQueueConfig>) {
    if (config) {
      this.defaultConfig = { ...this.defaultConfig, ...config };
    }
  }

  /**
   * Configure queue for a specific service
   */
  configureService(
    serviceName: string,
    config: Partial<RequestQueueConfig>,
  ): void {
    this.serviceConfigs.set(serviceName, { ...this.defaultConfig, ...config });
  }

  async intercept(
    direction: "inbound" | "outbound",
    message: RpcEnvelope | RpcResponse,
    next: () => Promise<any>,
    options?: { priority?: "normal" | "high" },
  ): Promise<any> {
    // Only apply to outbound RPC calls
    if (direction !== "outbound" || !this.isRpcCall(message)) {
      return next();
    }

    const serviceName = message.service || "default";
    const queue = this.getQueue(serviceName);

    return queue.process(message, next, options);
  }

  private getQueue(serviceName: string): RequestQueue {
    if (!this.queues.has(serviceName)) {
      const config = this.serviceConfigs.get(serviceName) || this.defaultConfig;
      const queue = new RequestQueue(config);
      this.queues.set(serviceName, queue);
    }

    return this.queues.get(serviceName)!;
  }

  private isRpcCall(
    message: RpcEnvelope | RpcResponse,
  ): message is RpcEnvelope {
    return "kind" in message && message.kind === "call";
  }

  /**
   * Get metrics for a specific service queue
   */
  getMetrics(serviceName: string = "default"): RequestQueueMetrics {
    const queue = this.queues.get(serviceName);
    if (!queue) {
      return {
        activeRequests: 0,
        queuedRequests: 0,
        totalProcessed: 0,
        totalRejected: 0,
        averageWaitTime: 0,
      };
    }

    return queue.getMetrics();
  }

  /**
   * Get metrics for all service queues
   */
  getAllMetrics(): Record<string, RequestQueueMetrics> {
    const metrics: Record<string, RequestQueueMetrics> = {};

    for (const [serviceName, queue] of this.queues) {
      metrics[serviceName] = queue.getMetrics();
    }

    return metrics;
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    for (const queue of this.queues.values()) {
      queue.destroy();
    }
    this.queues.clear();
  }
}
